import { useState, useEffect, useRef } from "react";
import { PlatformIcon } from "@/components/PlatformIcon";

type TabKey = "past" | "brand" | "host";

const tabs: { key: TabKey; label: string; icon: string }[] = [
  { key: "past", label: "Past Sessions", icon: "📅" },
  { key: "brand", label: "Brand Analytics", icon: "📊" },
  { key: "host", label: "Host Intelligence", icon: "🎯" },
];

/* ─── Animated counter ─── */
function AnimUp({ target, prefix = "", suffix = "" }: { target: number; prefix?: string; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<number>();
  useEffect(() => {
    const start = performance.now();
    const step = (now: number) => {
      const p = Math.min((now - start) / 1200, 1);
      setCount(Math.round(target * p));
      if (p < 1) ref.current = requestAnimationFrame(step);
    };
    ref.current = requestAnimationFrame(step);
    return () => { if (ref.current) cancelAnimationFrame(ref.current); };
  }, [target]);
  return <>{prefix}{count.toLocaleString()}{suffix}</>;
}

/* ─── Score badge ─── */
function ScoreBadge({ score }: { score: number }) {
  const color = score >= 85 ? "bg-success/20 text-success" : score >= 70 ? "bg-primary/20 text-primary" : "bg-warning/20 text-warning";
  return (
    <span className={`inline-flex items-center justify-center h-6 w-6 rounded-full text-[10px] font-bold ${color}`}>
      {score}
    </span>
  );
}

/* ─── Past Sessions Tab ─── */
const pastSessions = [
  { title: "Niacinamide Launch Live", date: "2025-03-08", duration: "43min", event: "Ramadan Sale", platforms: ["tiktok", "shopee"], host: "Alya Putri", avatar: -1, initial: "A", gmv: "18.9M", orders: 312, conv: "6.8%", viewers: "8.321", score: 87 },
  { title: "Brightening Routine Live", date: "2025-03-07", duration: "55min", event: "Ramadan Sale", platforms: ["tiktok"], host: "Sarah Amelia", avatar: -1, initial: "S", gmv: "28.4M", orders: 485, conv: "7.2%", viewers: "11.200", score: 94 },
  { title: "Sunscreen Essentials", date: "2025-03-06", duration: "38min", event: "Flash Sale Friday", platforms: ["shopee"], host: "Rani Wijaya", avatar: -1, initial: "R", gmv: "12.8M", orders: 198, conv: "5.1%", viewers: "6.500", score: 72 },
  { title: "Eye Cream Special", date: "2025-03-05", duration: "42min", event: "", platforms: ["tiktok", "shopee"], host: "Dina Kartika", avatar: -1, initial: "D", gmv: "8.9M", orders: 142, conv: "4.1%", viewers: "5.430", score: 61 },
  { title: "Body Care Bundle Live", date: "2025-03-04", duration: "48min", event: "Flash Sale Friday", platforms: ["tiktok"], host: "Alya Putri", avatar: -1, initial: "A", gmv: "22.1M", orders: 378, conv: "6.5%", viewers: "9.800", score: 85 },
  { title: "Flash Sale Night Live", date: "2025-03-03", duration: "35min", event: "", platforms: ["tiktok"], host: "Maya Permata", avatar: -1, initial: "M", gmv: "11.2M", orders: 189, conv: "5.9%", viewers: "6.200", score: 78 },
];

function PlatformDot({ type }: { type: string }) {
  return <PlatformIcon type={type} size={16} />;
}

function PastSessionsContent() {
  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Stats */}
      <div className="grid grid-cols-4 divide-x divide-border border-b border-border">
        {[
          { label: "SESSIONS", value: <AnimUp target={8} />, sub: "Last 7 days" },
          { label: "TOTAL GMV", value: <><AnimUp target={155} prefix="IDR " suffix=".4M" /></>, sub: "2.555 orders" },
          { label: "AVG CONVERSION", value: <AnimUp target={6} suffix=".1%" />, sub: "View → Order" },
          { label: "AVG AI SCORE", value: <AnimUp target={81} />, sub: "Excellent" },
        ].map((s, i) => (
          <div key={i} className="p-4">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">{s.label}</div>
            <div className="text-[20px] font-semibold text-foreground mt-1">{s.value}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Table header */}
      <div className="grid items-center px-4 h-9 border-b border-border text-[9px] uppercase tracking-wider text-muted-foreground font-medium"
        style={{ gridTemplateColumns: "1.8fr 0.7fr 1fr 0.8fr 0.6fr 0.6fr 0.6fr 0.4fr" }}>
        <div>Session</div>
        <div>Platform</div>
        <div>Host</div>
        <div className="text-right">GMV</div>
        <div className="text-right">Orders</div>
        <div className="text-right">Conv.</div>
        <div className="text-right">Viewers</div>
        <div className="text-right">Score</div>
      </div>

      {/* Rows */}
      <div className="flex-1 overflow-hidden">
        {pastSessions.map((s, i) => (
          <div key={i} className="grid items-center px-4 h-14 border-b border-border hover:bg-muted/30 transition-colors"
            style={{ gridTemplateColumns: "1.8fr 0.7fr 1fr 0.8fr 0.6fr 0.6fr 0.6fr 0.4fr" }}>
            <div className="min-w-0">
              <div className="text-[12px] font-medium text-foreground truncate">{s.title}</div>
              <div className="text-[9px] text-muted-foreground">{s.date} · {s.duration}{s.event ? ` · ${s.event}` : ""}</div>
            </div>
            <div className="flex items-center gap-1">
              {s.platforms.map(p => <PlatformDot key={p} type={p} />)}
            </div>
            <div className="flex items-center gap-2 min-w-0">
              <div className="h-5 w-5 rounded-full bg-muted-foreground/15 flex items-center justify-center text-[9px] font-medium text-muted-foreground shrink-0">
                {s.initial}
              </div>
              <span className="text-[11px] text-foreground truncate">{s.host}</span>
            </div>
            <div className="text-right text-[11px] font-semibold text-foreground">IDR {s.gmv}</div>
            <div className="text-right text-[11px] text-foreground">{s.orders}</div>
            <div className="text-right text-[11px] text-foreground">{s.conv}</div>
            <div className="text-right text-[11px] text-foreground">{s.viewers}</div>
            <div className="text-right"><ScoreBadge score={s.score} /></div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Brand Analytics Tab ─── */
const weeklyGMV = [
  { label: "Feb W1", value: 112 },
  { label: "Feb W2", value: 145 },
  { label: "Feb W3", value: 178 },
  { label: "Feb W4", value: 192 },
  { label: "Mar W1", value: 215 },
];
const maxGMV = Math.max(...weeklyGMV.map(w => w.value));

function BrandAnalyticsContent() {
  const [barHeight, setBarHeight] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setBarHeight(100), 100);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Stats */}
      <div className="grid grid-cols-4 divide-x divide-border border-b border-border">
        {[
          { label: "TOTAL SESSIONS", value: <AnimUp target={48} />, sub: "All time" },
          { label: "TOTAL GMV", value: <><AnimUp target={1} prefix="IDR " suffix=".4B" /></>, sub: "18.200 total orders" },
          { label: "AVG CONVERSION", value: <AnimUp target={6} suffix=".1%" />, sub: "View → Order" },
          { label: "AVG REV/VIEWER", value: <><AnimUp target={2} prefix="IDR " suffix=".156" /></>, sub: "Per session avg" },
        ].map((s, i) => (
          <div key={i} className="p-4">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium">{s.label}</div>
            <div className="text-[20px] font-semibold text-foreground mt-1">{s.value}</div>
            <div className="text-[10px] text-muted-foreground mt-0.5">{s.sub}</div>
          </div>
        ))}
      </div>

      <div className="p-4 space-y-4">
        {/* Weekly GMV trend chart */}
        <div className="border border-border rounded-lg p-4">
          <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium mb-4">Weekly GMV Trend</div>
          <div className="flex items-end gap-3 h-[120px]">
            {weeklyGMV.map((w, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                <div className="w-full relative" style={{ height: 100 }}>
                  <div
                    className="absolute bottom-0 w-full rounded-t bg-foreground/80 transition-all duration-1000 ease-out"
                    style={{ height: `${(w.value / maxGMV) * barHeight}%` }}
                  />
                </div>
                <span className="text-[9px] text-muted-foreground">{w.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom row: Platform split + Top performers */}
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-border rounded-lg p-4">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium mb-3">Platform Split</div>
            <div className="space-y-3">
              {[
                { name: "TikTok", platform: "tiktok", amount: "IDR 823.6M", pct: 58, color: "bg-foreground/70" },
                { name: "Shopee", platform: "shopee", amount: "IDR 454.4M", pct: 32, color: "bg-destructive/70" },
                { name: "Both (Simulcast)", platform: "both", amount: "IDR 142.0M", pct: 10, color: "bg-success/70" },
              ].map((p) => (
                <div key={p.name}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      {p.platform === "both" ? (
                        <span className="flex gap-0.5"><PlatformIcon type="tiktok" size={12} /><PlatformIcon type="shopee" size={12} /></span>
                      ) : (
                        <PlatformIcon type={p.platform} size={14} />
                      )}
                      <span className="text-[11px] text-foreground">{p.name}</span>
                    </div>
                    <span className="text-[10px] text-muted-foreground">{p.amount} ({p.pct}%)</span>
                  </div>
                  <div className="w-full h-1.5 rounded-full bg-muted overflow-hidden">
                    <div className={`h-full rounded-full ${p.color} transition-all duration-1000`} style={{ width: `${p.pct}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-border rounded-lg p-4">
            <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium mb-3">Top Performers</div>
            <div className="space-y-2.5">
              {[
                { label: "Best Host", value: "Sarah Amelia", icon: "👤" },
                { label: "Best Product", value: "Niacinamide Serum", icon: "🏷" },
                { label: "Best Campaign", value: "Ramadan Sale", icon: "🏆" },
                { label: "Avg Watch Time", value: "72s", icon: "⏱" },
                { label: "Avg Viewers", value: "8.340", icon: "👁" },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[11px]">{item.icon}</span>
                    <span className="text-[10px] text-muted-foreground">{item.label}</span>
                  </div>
                  <span className="text-[11px] font-semibold text-foreground">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Host Intelligence Tab ─── */
const hostIntelData = [
  { name: "Sarah Amelia", initial: "S", score: 94, gmv: "28.4M", sessions: 12, conv: "7.2%", trend: "+18%", topProduct: "Brightening Serum" },
  { name: "Alya Putri", initial: "A", score: 87, gmv: "22.1M", sessions: 15, conv: "6.8%", trend: "+12%", topProduct: "Niacinamide Toner" },
  { name: "Rani Wijaya", initial: "R", score: 78, gmv: "12.8M", sessions: 8, conv: "5.1%", trend: "+5%", topProduct: "Sunscreen SPF50" },
  { name: "Dina Kartika", initial: "D", score: 72, gmv: "8.9M", sessions: 6, conv: "4.1%", trend: "-2%", topProduct: "Eye Cream" },
  { name: "Maya Permata", initial: "M", score: 78, gmv: "11.2M", sessions: 4, conv: "5.9%", trend: "New", topProduct: "Body Lotion" },
];

function HostIntelligenceContent() {
  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border">
        <div className="text-[13px] font-medium text-foreground">Host Performance Ranking</div>
        <div className="text-[10px] text-muted-foreground mt-0.5">AI-scored performance based on GMV, conversion rate, viewer engagement, and session consistency</div>
      </div>

      {/* Cards */}
      <div className="p-4 space-y-3">
        {hostIntelData.map((host, i) => (
          <div key={i} className="border border-border rounded-lg p-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-start gap-3">
              {/* Rank */}
              <div className="text-[18px] font-bold text-muted-foreground/40 w-6 text-center shrink-0">
                {i + 1}
              </div>
              {/* Avatar */}
              <div className="h-8 w-8 rounded-full bg-muted-foreground/15 flex items-center justify-center text-[11px] font-medium text-muted-foreground shrink-0">
                {host.initial}
              </div>
              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-medium text-foreground">{host.name}</span>
                  <ScoreBadge score={host.score} />
                  <span className={`text-[9px] font-medium ${host.trend.startsWith("+") ? "text-success" : host.trend === "New" ? "text-primary" : "text-destructive"}`}>
                    {host.trend.startsWith("+") || host.trend.startsWith("-") ? `${host.trend} vs last month` : host.trend}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1.5">
                  <div>
                    <span className="text-[9px] text-muted-foreground">GMV</span>
                    <span className="text-[11px] font-semibold text-foreground ml-1.5">IDR {host.gmv}</span>
                  </div>
                  <div>
                    <span className="text-[9px] text-muted-foreground">Sessions</span>
                    <span className="text-[11px] font-semibold text-foreground ml-1.5">{host.sessions}</span>
                  </div>
                  <div>
                    <span className="text-[9px] text-muted-foreground">Conv.</span>
                    <span className="text-[11px] font-semibold text-foreground ml-1.5">{host.conv}</span>
                  </div>
                  <div>
                    <span className="text-[9px] text-muted-foreground">Top Product</span>
                    <span className="text-[11px] text-foreground ml-1.5">{host.topProduct}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Compare Sessions View ─── */
const compareMetrics = [
  { label: "GMV", a: "IDR 28.4M", b: "IDR 12.8M", aWin: true },
  { label: "Orders", a: "485", b: "198", aWin: true },
  { label: "Conversion Rate", a: "7.2%", b: "5.1%", aWin: true },
  { label: "Viewers", a: "11.200", b: "6.500", aWin: true },
  { label: "Peak Viewers", a: "15.800", b: "9.200", aWin: true },
  { label: "Add to Cart", a: "3.200", b: "1.450", aWin: true },
  { label: "Avg Watch Time", a: "82s", b: "64s", aWin: true },
  { label: "Comments", a: "612", b: "289", aWin: true },
  { label: "Revenue / Viewer", a: "IDR 2.536", b: "IDR 1.969", aWin: true },
  { label: "Revenue / Minute", a: "IDR 516.0K", b: "IDR 337.0K", aWin: true },
  { label: "Session score", a: "94", b: "72", aWin: true },
  { label: "Duration", a: "55 min", b: "38 min", aWin: false },
];

function CompareSessionsContent({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex-1 flex flex-col min-w-0">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border flex items-center gap-3">
        <button onClick={onBack} className="text-muted-foreground hover:text-foreground transition-colors text-[14px]">←</button>
        <div>
          <div className="text-[13px] font-semibold text-foreground">Session Comparison</div>
          <div className="text-[10px] text-muted-foreground">Side-by-side analysis of 2 sessions</div>
        </div>
      </div>

      {/* Comparison table */}
      <div className="flex-1 overflow-hidden">
        {/* Column headers */}
        <div className="grid border-b border-border" style={{ gridTemplateColumns: "1.2fr 1fr 1fr" }}>
          <div className="px-4 py-3 text-[9px] uppercase tracking-wider text-muted-foreground font-medium">Metric</div>
          <div className="px-4 py-3 border-l border-border">
            <div className="text-[12px] font-medium text-foreground">Brightening Routine Live</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <PlatformDot type="tiktok" />
              <span className="text-[9px] text-muted-foreground">2025-03-07 · Sarah Amelia</span>
            </div>
          </div>
          <div className="px-4 py-3 border-l border-border">
            <div className="text-[12px] font-medium text-foreground">Sunscreen Essentials</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <PlatformDot type="shopee" />
              <span className="text-[9px] text-muted-foreground">2025-03-06 · Rani Wijaya</span>
            </div>
          </div>
        </div>

        {/* Rows */}
        {compareMetrics.map((m, i) => (
          <div key={i} className="grid border-b border-border hover:bg-muted/30 transition-colors" style={{ gridTemplateColumns: "1.2fr 1fr 1fr" }}>
            <div className="px-4 py-2.5 text-[11px] text-muted-foreground">{m.label}</div>
            <div className={`px-4 py-2.5 border-l border-border text-[12px] font-semibold text-center ${m.aWin ? "text-success" : "text-foreground"}`}>
              {m.a}
            </div>
            <div className={`px-4 py-2.5 border-l border-border text-[12px] font-semibold text-center ${!m.aWin ? "text-success" : "text-foreground"}`}>
              {m.b}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Main Component ─── */
export type AnalyticsTabKey = TabKey;

export function AnalyticsMockup({ onTabChange }: { onTabChange?: (tab: TabKey) => void }) {
  const [activeTab, setActiveTab] = useState<TabKey>("past");
  const [showCompare, setShowCompare] = useState(false);

  const handleTabChange = (tab: TabKey) => {
    setActiveTab(tab);
    setShowCompare(false);
    onTabChange?.(tab);
  };

  // No auto-cycle — tabs only change on user click

  return (
    <div className="relative" style={{ overflow: "visible" }}>
      <div className="relative z-10 rounded-t-xl border border-b-0 border-border bg-card overflow-hidden">
        <div className="flex flex-col min-h-[460px]">
          {/* Tab bar */}
          <div className="flex items-center gap-1 px-4 h-10 border-b border-border">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => handleTabChange(tab.key)}
                className={`flex items-center gap-1.5 px-2.5 h-7 rounded text-[11px] transition-colors ${
                  activeTab === tab.key && !showCompare
                    ? "bg-accent text-foreground font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
                }`}
              >
                <span className="text-[10px]">{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            ))}
            <button
              onClick={() => setShowCompare(v => !v)}
              className={`ml-auto flex items-center gap-1.5 px-2.5 h-7 rounded text-[11px] transition-colors ${
                showCompare
                  ? "bg-success/15 text-success font-medium"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50"
              }`}
            >
              <span>⚡</span>
              <span>{showCompare ? "Compare (2/5)" : "Compare Sessions"}</span>
            </button>
          </div>

          {/* Content */}
          <div className="animate-fade-in" key={showCompare ? "compare" : activeTab}>
            {showCompare ? (
              <CompareSessionsContent onBack={() => setShowCompare(false)} />
            ) : (
              <>
                {activeTab === "past" && <PastSessionsContent />}
                {activeTab === "brand" && <BrandAnalyticsContent />}
                {activeTab === "host" && <HostIntelligenceContent />}
              </>
            )}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      </div>
    </div>
  );
}
